import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { AppConfig } from "../../../AppConfig";
import {
  BASIC_BROWN,
  BASIC_WHITE,
  BASIC_YELLOW,
} from "../../../constants/colors";
import { PageRoutes } from "../../../constants/routes";
import { LocalStorageManager } from "../../../core/storage/LocalStorageManager";
import { appActions } from "../../../ducks/app";
import {
  AppCoupon,
  AppliedCoupon,
  couponActions,
  couponSelectors,
  CouponType,
} from "../../../ducks/coupon";
import { Button } from "../../atoms/Button";
import { fadein, PopupModalContainer } from "./PopupModal.styles";

export const AppCouponModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const dispatch = useDispatch();
  const location = useLocation();
  const appCoupons = useSelector(couponSelectors.appCoupons);
  const selectedAppCoupon = useSelector(couponSelectors.selectedAppCoupons);
  const appliedCoupons = useSelector(couponSelectors.appliedCoupons);

  const getAppCoupons = useCallback(
    (couponCode: string, isRepeat?: boolean) => {
      dispatch(appActions.showLoading());
      dispatch(
        couponActions.getLimitedCoupons.started({
          coupon_code: [couponCode],
          onComplete: () => dispatch(appActions.hideLoading()),
          onSuccess: (coupons) => {
            if (coupons.length === 0) return;

            setIsOpen(true);
          },
          onError: () => {
            const unselectedAppCoupons: string[] =
              LocalStorageManager.Instance.getObject(
                AppConfig.Instance.LocalStorageKey["unselectedAppCoupons"],
              ) ?? [];

            const newUnselectedAppCoupons = unselectedAppCoupons.filter(
              (coupon) => coupon !== unselectedAppCoupons[0],
            );

            LocalStorageManager.Instance.saveObject(
              AppConfig.Instance.LocalStorageKey["unselectedAppCoupons"],
              newUnselectedAppCoupons,
            );

            if (newUnselectedAppCoupons.length === 0) {
              if (isRepeat) dispatch(appActions.closeModal());
              return;
            }

            dispatch(appActions.closeModal());
            getAppCoupons(newUnselectedAppCoupons[0], isRepeat);
          },
        }),
      );
    },
    [],
  );

  const validateSelectCoupon = useCallback(
    (
      coupon: AppCoupon,
      selectedAppCoupon: AppCoupon[] | null,
      appliedCoupons: AppliedCoupon[] | null,
    ): boolean => {
      // * if the app multi or app coupon is already selected or applied, don't save it again
      const isAppKind = coupon.kind === CouponType.App;
      const isAppMultiKind = coupon.kind === CouponType.AppMulti;
      const isFreeCoupon = coupon.isFreeCoupon;
      const isFreePath = coupon.isExclusive;

      const shouldValidate =
        (isFreeCoupon && isAppMultiKind) ||
        (!isFreeCoupon && (isAppKind || isAppMultiKind));

      const isAlreadySelected = selectedAppCoupon?.some((c) =>
        isAppKind
          ? c.code.substring(0, 5) === coupon.code.substring(0, 5) &&
            c.kind === CouponType.App
          : isAppMultiKind
            ? c.code === coupon.code && c.kind === CouponType.AppMulti
            : false,
      );

      const isAlreadyApplied = appliedCoupons?.some((c) =>
        isAppKind
          ? c.code.substring(0, 5) === coupon.code.substring(0, 5) &&
            coupon.kind === CouponType.App
          : isAppMultiKind
            ? c.code === coupon.code && coupon.kind === CouponType.AppMulti
            : false,
      );

      const isAlreadyFreePassApplied =
        appliedCoupons?.some((c) => c.code === coupon.code && isFreePath) ||
        selectedAppCoupon?.some((c) => c.code === coupon.code && isFreePath);

      if (
        (shouldValidate && (isAlreadySelected || isAlreadyApplied)) ||
        (isFreePath && isAlreadyFreePassApplied)
      ) {
        setIsOpen(false);
        const title = isFreePath
          ? "このクーポンは同時に\n複数枚ご利用いただけません"
          : isAppKind
            ? "このクーポンは一枚のみお使いいただけます。"
            : "すでに同一のクーポンがあります。\nカート内で該当のメニューの\n数量を変更してください。";
        dispatch(
          appActions.showModal.started({
            title,
            subTitle: `[${coupon.code}]: ${coupon.name}`,
            nextButtonLabel: "閉じる",
            handleNext: () => {
              dispatch(appActions.closeModal());
            },
          }),
        );
        return false;
      }
      return true;
    },
    [],
  );

  const handleSelectCoupon = useCallback(
    (coupon: AppCoupon) => {
      if (!validateSelectCoupon(coupon, selectedAppCoupon, appliedCoupons))
        return;
      dispatch(
        couponActions.saveSelectedAppCoupon.started({
          coupon,
          onSuccess: (unselectedAppCoupons) => {
            setIsOpen(false);

            if (unselectedAppCoupons.length === 0) return;

            getAppCoupons(unselectedAppCoupons[0], true);
          },
        }),
      );
    },
    [getAppCoupons, selectedAppCoupon, appliedCoupons],
  );

  useEffect(() => {
    if (location.pathname !== PageRoutes.HOME) return;

    const unselectedAppCoupons: string[] =
      LocalStorageManager.Instance.getObject(
        AppConfig.Instance.LocalStorageKey["unselectedAppCoupons"],
      ) ?? [];

    if (unselectedAppCoupons.length === 0) return;

    getAppCoupons(unselectedAppCoupons[0]);
  }, [location.pathname]);

  if (!isOpen || !appCoupons || appCoupons.length === 0) return null;

  return (
    <PopupModalContainer $isOpen={isOpen}>
      <ModalContainer $isOpen={isOpen}>
        <ModalHeader>アプリ連携クーポン</ModalHeader>

        <ModalContent>
          <Text>指定のメニューから、おひとつ選択して進んでください♪</Text>

          <AppCouponList>
            {appCoupons.map((coupon) => (
              <ButtonWrapper key={coupon.code}>
                <Button
                  btnStyle="primary"
                  text={coupon.name}
                  onClick={() => handleSelectCoupon(coupon)}
                  size="md"
                />
              </ButtonWrapper>
            ))}
          </AppCouponList>
        </ModalContent>
      </ModalContainer>
    </PopupModalContainer>
  );
};

const ModalContainer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  background-color: white;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transform: ${({ $isOpen }) =>
    $isOpen
      ? "translate(-50%, -50%) scale(1, 1)"
      : "translate(-50%, -50%) scale(0.9, 0.9)"};
  animation: ${fadein} 0.5s forwards;
`;

const Text = styled.div`
  margin-bottom: 40px;
`;

const ModalHeader = styled.div`
  position: relative;
  background-color: ${BASIC_BROWN};
  padding: 15px 0 15px 0;
  border-bottom: 4px solid ${BASIC_YELLOW};
  text-align: center;
  color: ${BASIC_WHITE};
  font-size: 20px;
`;

const ModalContent = styled.div`
  padding: 45px 15px;
  text-align: center;
`;

const AppCouponList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 20px;

  button {
    width: auto;
    margin: 0 0 25px 0;
    line-height: 1.2;
  }
`;

const ButtonWrapper = styled.div`
  button {
    width: 300px;
    font-size: 16px;
    font-weight: 600;
    padding: 12px 20px;
    line-height: 1.4;
    white-space: break-spaces;
  }
`;
